import * as React from 'react';
import { GetProviderServicesResponse } from '@leagueplatform/wallet-api';
import {
  Box,
  StackLayout,
  ParagraphText,
  HeadingText,
} from '@leagueplatform/genesis-core';
import { Accordion } from '@leagueplatform/web-common-components';
import { useIntl } from '@leagueplatform/locales';
import {
  EVENT_NAME,
  PRODUCT_AREA,
  SCREEN_NAMES,
} from '@leagueplatform/analytics';
import { useWalletDetailAnalytics } from '#pages/wallet-detail/hooks/use-wallet-detail-analytics.hook.ts';
import { DefinitionPopover } from '#components/definition-popover/definition-popover.component.tsx';

interface NetworkPanelProps {
  services: GetProviderServicesResponse['provider_services'][
    | 'in_network'
    | 'out_of_network'];
  networkName?: string;
  onToggle?: (serviceName: string, state: 'expand' | 'collapse') => void;
}

export function NetworkPanel({
  services = [],
  networkName,
  onToggle,
}: NetworkPanelProps) {
  const { formatMessage } = useIntl();
  const firstService = services?.[0]?.service_name?.replaceAll(' ', '+');
  const defaultValue = firstService ? [firstService] : [];
  const [activePanels, setActivePanels] =
    React.useState<string[]>(defaultValue);
  const trackAnalyticsEvent = useWalletDetailAnalytics();

  const handleOnToggle = React.useCallback(
    (currentPanels: string[]) => {
      if (currentPanels.length > activePanels.length) {
        // Accordion component always adds the newest clicked trigger to the end of currentPanels array
        const clickedPanel = currentPanels[currentPanels.length - 1];
        if (onToggle) {
          onToggle(clickedPanel.replaceAll('+', ' '), 'expand');
        }
        setActivePanels(currentPanels);

        return;
      }

      // Get the panel that does not exist in currentPanels
      const [clickedPanel] = activePanels.filter(
        (x) => !currentPanels.includes(x),
      );

      if (onToggle) {
        onToggle(clickedPanel.replaceAll('+', ' '), 'collapse');
      }
      setActivePanels(currentPanels);
    },
    [activePanels, onToggle],
  );

  if (services === null || services.length === 0) {
    return (
      <ParagraphText css={{ typography: '$bodyOne' }}>
        {formatMessage(
          { id: 'NETWORK_MISSING_COVERAGE' },
          { network: networkName },
        )}
      </ParagraphText>
    );
  }

  return (
    <Accordion.Root
      type="multiple"
      defaultValue={defaultValue}
      onValueChange={handleOnToggle}
    >
      {services.map(({ service_name: serviceName = '', providers = [] }) => (
        <Accordion.Item
          value={serviceName.replaceAll(' ', '+')}
          key={serviceName}
        >
          <Accordion.Header size="md" level="3">
            <Accordion.Trigger>{serviceName}</Accordion.Trigger>
          </Accordion.Header>
          <Accordion.Content>
            <StackLayout
              spacing="$half"
              css={{
                width: '100%',
              }}
            >
              {providers.map(({ title, description, definition }) => (
                <Box
                  as="section"
                  key={title}
                  css={{
                    backgroundColor: '$surfaceBackgroundSecondary',
                    padding: '$one',
                    marginInlineStart: '$oneAndHalf',
                    width: 'calc(100% - $oneAndHalf)',
                  }}
                >
                  <Box
                    css={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <HeadingText
                        level="4"
                        size="md"
                        css={{ typography: '$subtitleTwo' }}
                      >
                        {title}
                      </HeadingText>
                      <ParagraphText css={{ typography: '$bodyTwo' }}>
                        {description}
                      </ParagraphText>
                    </div>
                    {definition?.description && (
                      <DefinitionPopover
                        description={definition.description}
                        triggerLabel={formatMessage({
                          id: 'PROVIDER_SERVICE_POPOVER_BUTTON_NAME',
                        })}
                        onClick={() => {
                          trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
                            screen_name: SCREEN_NAMES.BENEFIT_PAGE,
                            product_area: PRODUCT_AREA.WALLET,
                            detail: 'clicked on tooltip for more info',
                            sub_detail: `Tooltip for ${serviceName} - ${title}`,
                          });
                        }}
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </StackLayout>
          </Accordion.Content>
        </Accordion.Item>
      ))}
    </Accordion.Root>
  );
}
